import React, { useEffect, useState, useCallback } from "react";
import { withStyles, makeStyles } from "@material-ui/core/styles";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Backdrop,
  CircularProgress,
  Button,
  Box,
  Typography,
  Tooltip,
  TextField,
} from "@material-ui/core";

import { privateList } from "src/adminModel/privateSale";

import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import SearchIcon from "@material-ui/icons/Search";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import CardMembershipOutlinedIcon from "@material-ui/icons/CardMembershipOutlined";

// =====================================================
// PAGINATION
// =====================================================

const LIMIT = 20;

// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },

  toolbar: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #eeeeee",
  },

  searchWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },

  searchField: {
    width: 300,
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": { borderRadius: 6 },
    "& .MuiInputBase-input": { fontSize: 13 },
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },

  searchButton: {
    height: 40,
    minWidth: 100,
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  },

  container: { maxHeight: 650 },

  table: { minWidth: 1100 },

  tableRow: { "&:hover": { backgroundColor: "#fafafa" } },

  cellText: {
    maxWidth: 240,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 13,
    color: "#444",
  },

  artistText: { fontSize: 13, fontWeight: 500, color: "#222" },

  idButton: {
    textTransform: "none",
    fontWeight: 600,
    padding: 0,
    minWidth: 0,
    justifyContent: "flex-start",
    fontSize: 13,
  },

  actionWrapper: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },

  actionButton: {
    textTransform: "none",
    fontSize: 11,
    minWidth: 0,
    height: 30,
    padding: "0 10px",
    borderRadius: 5,
    boxShadow: "none",
  },

  pagination: {
    padding: theme.spacing(2),
    borderTop: "1px solid #eeeeee",
    backgroundColor: "#fafafa",
  },

  paginationButton: {
    textTransform: "none",
    borderRadius: 6,
    fontWeight: 600,
    minWidth: 90,
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  },

  pageNumber: {
    fontSize: 13,
    fontWeight: 600,
    color: "#444",
    minWidth: 80,
    textAlign: "center",
  },

  emptyCell: { padding: "50px !important" },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// =====================================================
// HELPERS
// =====================================================

const stripHtml = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof document === "undefined") {
    return String(value).replace(/<[^>]*>/g, "");
  }

  const temp = document.createElement("div");
  temp.innerHTML = String(value);

  return temp.textContent || temp.innerText || "";
};

const escapeHtml = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const formatMoney = (value) =>
  parseFloat(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getToday = () =>
  new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const getRowValue = (row, keys, fallback = "") => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = row[keys[i]];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

/**
 * Accept every shape privateList might hand back.
 *
 * The API returns { sucess, msg, data: [...] }. Depending on the version
 * of adminModel/privateSale, privateList returns either that whole object
 * or just the array. The old code only accepted a bare array -- anything
 * else became [], and an empty page looks exactly like "no next page".
 *
 * Note the API's own typo: "sucess", not "success". Any code checking
 * `result.success` on this endpoint is always false.
 */
const toRows = (results) => {
  if (Array.isArray(results)) {
    return results;
  }

  if (results && Array.isArray(results.data)) {
    return results.data;
  }

  return [];
};

const openPrintWindow = (width) => {
  const printWindow = window.open("", "_blank", `width=${width},height=900`);

  if (!printWindow) {
    alert("Please allow pop-ups for this website.");
    return null;
  }

  return printWindow;
};

// =====================================================
// TABLE CELL
// =====================================================

const StyledTableCell = withStyles({
  root: {
    padding: "11px 14px",
    fontSize: "13px",
    color: "#333",
    borderBottom: "1px solid #e5e5e5",
    verticalAlign: "middle",
  },
  head: {
    backgroundColor: "#f7f7f7",
    color: "#222",
    fontWeight: 600,
    fontSize: "12px",
    padding: "13px 14px",
    borderBottom: "1px solid #d8d8d8",
    textTransform: "uppercase",
    letterSpacing: "0.2px",
  },
})(TableCell);

// =====================================================
// RECEIPT DOCUMENT
// =====================================================

const RECEIPT_STYLES = `
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }

  html, body { margin: 0; padding: 0; background: #e5e7eb; color: #000; }
  body { font-family: Arial, Helvetica, sans-serif; padding-bottom: 40px; }

  .topbar {
    position: sticky; top: 0; z-index: 1000;
    height: 60px; width: 100%;
    background: #fff;
    border-bottom: 1px solid #d1d5db;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    display: flex; align-items: center; justify-content: flex-end;
    padding: 0 20px;
  }

  .print-button {
    border: none; background: #1976d2; color: #fff;
    padding: 10px 18px; border-radius: 6px;
    font: 600 14px Arial, sans-serif; cursor: pointer;
  }
  .print-button:hover { background: #1565c0; }

  .paper {
    width: 210mm; min-height: 297mm;
    margin: 25px auto; padding: 18mm;
    background: #fff;
    box-shadow: 0 5px 25px rgba(0,0,0,0.15);
  }

  .editable { outline: none; cursor: text; border-radius: 2px; transition: background 0.15s ease; }
  .editable:hover { background: #fffde7; }
  .editable:focus { background: #fff8cc; box-shadow: inset 0 0 0 1px #d6b656; }

  .header {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding-bottom: 12px; border-bottom: 1px solid #000;
  }
  .logo { width: 180px; height: auto; object-fit: contain; }
  .contact { text-align: right; font-size: 10px; line-height: 1.5; }
  .contact p { margin: 0; }

  .document-title {
    text-align: center; font-size: 19px; font-weight: 700;
    margin: 23px 0 20px; text-transform: uppercase;
  }

  .info { margin-bottom: 18px; }
  .info-row { display: flex; margin-bottom: 6px; font-size: 12px; }
  .info-label { width: 80px; font-weight: 700; }
  .info-value { flex: 1; min-height: 18px; }

  .receipt-table { width: 100%; border-collapse: collapse; border: 0.5pt solid #000; }
  .receipt-table th {
    background: #f2f2f2; border: 0.5pt solid #000; padding: 8px;
    font-size: 10px; font-weight: 700; text-transform: uppercase; text-align: center;
  }
  .receipt-table td { border: 0.5pt solid #000; padding: 8px; vertical-align: top; page-break-inside: avoid; }

  .artist-cell { width: 20%; text-align: center; font-size: 11px; }
  .details-cell { width: 55%; text-align: center; }
  .amount-cell { width: 25%; text-align: center; font-size: 11px; font-weight: 600; }

  .artwork-image { width: 100%; height: 300px; object-fit: contain; display: block; margin: 0 auto; }
  .no-image {
    width: 100%; height: 300px; display: flex; align-items: center; justify-content: center;
    background: #f5f5f5; color: #777; font-size: 11px;
  }

  .artwork-details { margin-top: 10px; font-size: 11px; line-height: 1.7; }
  .artwork-title { font-size: 14px; font-weight: 600; margin-bottom: 4px; }

  .amount-section { display: flex; justify-content: space-between; align-items: center; font-size: 12px; }
  .subtotal-label { font-weight: 700; }

  .summary-label {
    border: 0.5pt solid #000; padding: 8px; text-align: right;
    font-size: 10px; font-weight: 600; text-transform: uppercase;
  }
  .summary-value { border: 0.5pt solid #000; padding: 8px; text-align: center; font-size: 11px; min-height: 18px; }

  .total-label {
    border: 0.5pt solid #000; padding: 10px 8px; text-align: right;
    font-size: 11px; font-weight: 700; text-transform: uppercase;
  }
  .total-value {
    border: 0.5pt solid #000; padding: 10px 8px; text-align: center;
    font-size: 12px; font-weight: 700; background: #f9f9f9; min-height: 20px;
  }

  .signature-section { margin-top: 75px; display: flex; justify-content: flex-end; page-break-inside: avoid; }
  .signature { width: 220px; text-align: center; }
  .signature-space { min-height: 25px; margin-bottom: 5px; }
  .signature-line {
    border-top: 0.5pt solid #000; padding-top: 5px;
    font-size: 11px; font-weight: 700; text-transform: uppercase;
  }

  .address { text-align: center; margin-top: 50px; font-size: 10px; line-height: 1.5; }
  .address p { margin: 2px 0; }

  @media print {
    html, body { background: #fff; }
    body { padding: 0; }
    .topbar { display: none !important; }
    .paper { margin: 0; box-shadow: none; }
    .editable:hover, .editable:focus { background: transparent !important; box-shadow: none !important; }
    .receipt-table tr, .signature-section, img { page-break-inside: avoid; }
  }
`;

const buildReceipt = (row) => {
  const artist = stripHtml(row.artist || "");
  const title = stripHtml(row.title || "");
  const medium = stripHtml(row.medium || "");
  const size = stripHtml(row.size || "");

  const customerName = stripHtml(
    getRowValue(row, [
      "name",
      "customer_name",
      "client_name",
      "buyer_name",
      "firstname",
      "first_name",
    ])
  );

  const receiptDate = getRowValue(row, [
    "date",
    "receipt_date",
    "transaction_date",
    "created_at",
    "signed_date",
  ]);

  const formattedDate = formatDate(receiptDate) || getToday();

  const amount = getRowValue(
    row,
    ["amount", "price", "sale_price", "selling_price", "total_amount"],
    0
  );

  const discount = getRowValue(row, ["discount", "discount_amount"], 0);

  const finalAmount = parseFloat(amount || 0) - parseFloat(discount || 0);

  const type = stripHtml(
    getRowValue(row, ["type", "sale_type", "transaction_type"], "Private Sale")
  );

  const year = stripHtml(getRowValue(row, ["year", "artwork_year", "date_year"]));

  const image = getRowValue(row, [
    "image",
    "image_url",
    "imageData",
    "image_data",
    "photo",
    "photo_url",
  ]);

  const imageHtml = image
    ? `<img src="${escapeHtml(image)}" alt="Artwork" class="artwork-image" />`
    : `<div class="no-image">No Image</div>`;

  const editable = `class="editable" contenteditable="true" spellcheck="false"`;

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>Private Sale Receipt</title>
        <style>${RECEIPT_STYLES}</style>
      </head>
      <body>
        <div class="topbar">
          <button class="print-button" onclick="window.print()">Print</button>
        </div>

        <div class="paper">
          <div class="header">
            <img src="/static/logo140.png" alt="Leon Gallery" class="logo" />
            <div class="contact">
              <p><b>www.leon-gallery.com</b></p>
              <p>info@leon-gallery.com</p>
              <p>leongallerymakati@gmail.com</p>
            </div>
          </div>

          <div class="document-title editable" contenteditable="true" spellcheck="false">
            Receipt
          </div>

          <div class="info">
            <div class="info-row">
              <div class="info-label">Name:</div>
              <div class="info-value editable" contenteditable="true" spellcheck="false">
                ${escapeHtml(customerName)}
              </div>
            </div>
            <div class="info-row">
              <div class="info-label">Date:</div>
              <div class="info-value editable" contenteditable="true" spellcheck="false">
                ${escapeHtml(formattedDate)}
              </div>
            </div>
          </div>

          <table class="receipt-table">
            <thead>
              <tr>
                <th class="artist-cell">Artist</th>
                <th class="details-cell">Details</th>
                <th class="amount-cell">Amount</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td class="artist-cell editable" contenteditable="true" spellcheck="false">
                  ${escapeHtml(artist)}
                </td>

                <td class="details-cell">
                  ${imageHtml}
                  <div class="artwork-details editable" contenteditable="true" spellcheck="false">
                    <div class="artwork-title">${escapeHtml(title)}</div>
                    <div>${escapeHtml(size)}</div>
                    <div>${escapeHtml(medium)}</div>
                    <div>${escapeHtml(type)}</div>
                    ${year ? `<div>${escapeHtml(year)}</div>` : ""}
                  </div>
                </td>

                <td class="amount-cell">
                  <div class="amount-section">
                    <span class="subtotal-label">Sub Total</span>
                    <span ${editable}>${formatMoney(amount)}</span>
                  </div>
                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colspan="2" class="summary-label">Discount</td>
                <td class="summary-value editable" contenteditable="true" spellcheck="false">
                  ${formatMoney(discount)}
                </td>
              </tr>
              <tr>
                <td colspan="2" class="total-label">Total</td>
                <td class="total-value editable" contenteditable="true" spellcheck="false">
                  ${formatMoney(finalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>

          <div class="signature-section">
            <div class="signature">
              <div class="signature-space editable" contenteditable="true" spellcheck="false"></div>
              <div class="signature-line editable" contenteditable="true" spellcheck="false">
                Authorized Signature
              </div>
            </div>
          </div>

          <div class="address">
            <p>G/F Eurovilla 1, Rufino Corner Legazpi Streets,</p>
            <p>Legazpi Village, Makati City</p>
            <p>Philippines</p>
            <p>Tel no. (632) 856-2781</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

// =====================================================
// CERTIFICATE DOCUMENT
// =====================================================

const CERTIFICATE_STYLES = `
  @page { size: Letter portrait; margin: 0; }
  * { box-sizing: border-box; }

  html, body {
    margin: 0; padding: 0; background: #e5e7eb; color: #000;
    font-family: Arial, Helvetica, sans-serif;
  }
  body { padding-top: 60px; padding-bottom: 30px; }

  .topbar {
    position: fixed; top: 0; left: 0; right: 0; height: 60px;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 20px; background: #fff;
    border-bottom: 1px solid #d1d5db;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    z-index: 9999;
  }
  .preview-title { font-size: 14px; font-weight: 600; color: #374151; }
  .print-button {
    border: none; background: #1976d2; color: #fff;
    padding: 10px 18px; border-radius: 7px;
    font-size: 14px; font-weight: 600; cursor: pointer;
  }
  .print-button:hover { background: #1565c0; }

  .paper {
    width: 215.9mm; height: 279.4mm;
    margin: 20px auto; padding: 12mm 14mm;
    background: #fff; position: relative;
    box-shadow: 0 5px 25px rgba(0,0,0,0.15);
    overflow: hidden;
    page-break-after: always; break-after: page;
  }

  .page-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding-bottom: 10px; border-bottom: 1px solid #000;
  }
  .logo { width: 150px; height: auto; display: block; }
  .contact { text-align: right; font-size: 8px; line-height: 1.5; color: #333; }

  .document-title {
    text-align: center; font-size: 20px; font-weight: 700;
    letter-spacing: 0.5px; margin: 18px 0 8px; text-transform: uppercase;
  }
  .document-subtitle {
    text-align: center; max-width: 155mm; margin: 0 auto;
    font-size: 11px; line-height: 1.5; color: #333;
  }

  .artwork-area {
    width: 100%; height: 92mm; margin-top: 12mm;
    display: flex; align-items: center; justify-content: center;
    padding: 5mm; border: 1px solid #e5e7eb; background: #fafafa;
  }
  .artwork { display: block; max-width: 100%; max-height: 82mm; width: auto; height: auto; object-fit: contain; }
  .no-image {
    width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
    color: #777; font-size: 11px;
  }

  .details { text-align: center; margin-top: 10mm; }
  .artist { font-size: 19px; font-weight: 700; line-height: 1.25; margin-bottom: 3mm; word-break: break-word; }
  .title { font-size: 14px; font-style: italic; line-height: 1.35; color: #222; word-break: break-word; }
  .year { font-size: 14px; line-height: 1.35; color: #222; }
  .medium, .size { font-size: 12px; line-height: 1.45; color: #333; }

  .signature-section { position: absolute; right: 18mm; bottom: 30mm; width: 55mm; text-align: center; }
  .signature-line { border-top: 1px solid #000; padding-top: 6px; }
  .signature-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }

  .footer {
    position: absolute; left: 14mm; right: 14mm; bottom: 10mm;
    text-align: center; font-size: 7.5px; color: #666; line-height: 1.5;
  }

  .editable { outline: none; border-radius: 2px; }
  .editable:hover { background: #fffde7; }
  .editable:focus { background: #fff8cc; box-shadow: inset 0 0 0 1px #d6b656; }

  @media print {
    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .topbar { display: none !important; }
    .paper { margin: 0 !important; box-shadow: none !important; }
    .paper:last-child { page-break-after: auto !important; break-after: auto !important; }
    .editable:hover, .editable:focus { background: transparent !important; box-shadow: none !important; }
  }
`;

const buildCertificate = (row) => {
  const artist = stripHtml(row.artist || "");
  const title = stripHtml(row.title || "");
  const medium = stripHtml(row.medium || "");
  const size = stripHtml(row.size || "");
  const year = stripHtml(getRowValue(row, ["year", "artwork_year", "date_year"]));

  const image = getRowValue(row, [
    "image",
    "image_url",
    "imageData",
    "image_data",
    "photo",
    "photo_url",
  ]);

  const artworkName = artist || title || "Certification";

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${escapeHtml(artworkName)}</title>
        <style>${CERTIFICATE_STYLES}</style>
      </head>
      <body>
        <div class="topbar">
          <div class="preview-title">Certificate Preview</div>
          <button class="print-button" onclick="window.print()">Print</button>
        </div>

        <div class="paper">
          <div class="page-header">
            <div>
              <img src="/static/logo140.png" class="logo" alt="Leon Gallery" />
            </div>
            <div class="contact">
              <div><b>www.leon-gallery.com</b></div>
              <div>leongallerymakati@gmail.com</div>
              <div>info@leon-gallery.com</div>
            </div>
          </div>

          <div class="document-title editable" contenteditable="true" spellcheck="false">
            CERTIFICATION
          </div>

          <div class="document-subtitle editable" contenteditable="true" spellcheck="false">
            This is to certify that the piece with picture and details below was purchased from Leon Gallery.
          </div>

          <div class="artwork-area">
            ${
              image
                ? `<img src="${escapeHtml(image)}" class="artwork" alt="Artwork" />`
                : `<div class="no-image">No Image</div>`
            }
          </div>

          <div class="details">
            <div class="artist editable" contenteditable="true" spellcheck="false">
              ${escapeHtml(artist)}
            </div>

            <span class="title editable" contenteditable="true" spellcheck="false">
              ${escapeHtml(title)}
            </span>

            <span class="year editable" contenteditable="true" spellcheck="false">
              ${year ? `, ${escapeHtml(year)}` : ""}
            </span>

            <br />

            <div class="medium editable" contenteditable="true" spellcheck="false">
              ${escapeHtml(medium)}
            </div>

            <div class="size editable" contenteditable="true" spellcheck="false">
              ${escapeHtml(size)}
            </div>
          </div>

          <div class="signature-section">
            <div class="signature-line">
              <div class="signature-label editable" contenteditable="true" spellcheck="false">
                Authorized Signature
              </div>
            </div>
          </div>

          <div class="footer">
            <div>
              G/F Eurovilla 1, Rufino Corner Legazpi Streets, Legazpi Village, Makati City, Philippines
            </div>
            <div>Tel no. (632) 856-2781 | www.leon-gallery.com</div>
          </div>
        </div>
      </body>
    </html>
  `;
};

// =====================================================
// COMPONENT
// =====================================================

export default function CustomizedTables() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState(0);
  const [getData, setGetdata] = useState([]);
  const [inputKeyword, setInputKeyword] = useState("");
  const [activeKeyword, setActiveKeyword] = useState("");

  // ONE source of truth for "is there a next page".
  //
  // The old code had two different rules for the same question:
  //   the button:  disabled={getData.length <  LIMIT}
  //   the click:   if (getData.length === LIMIT) ...
  // If the API returned more than LIMIT rows (it ignores `limit`), the
  // button looked ENABLED but clicking it did nothing. If it returned
  // fewer, the button was disabled. Either way: "walang next page".
  const [hasMore, setHasMore] = useState(false);

  // =====================================================
  // FETCH
  //
  // Asks for LIMIT + 1 rows. The extra row is never shown -- it only
  // answers "is there anything after this page?". If it comes back, there
  // is a next page. This is exact, unlike guessing from `length === LIMIT`,
  // which says "maybe" on the last full page and sends the user to an
  // empty one.
  // =====================================================

  const FetchAuction = useCallback(async (currentStart, currentKeyword) => {
    setLoading(true);

    try {
      const results = await privateList({
        keyword: currentKeyword,
        group: "PRIVATE_SALE",
        id: undefined,
        start: currentStart,
        limit: LIMIT + 1,
      });

      const rows = toRows(results);

      // If the backend honours `limit`, rows.length is at most LIMIT + 1
      // and the probe is exact. If it ignores `limit` and returns more,
      // rows.length > LIMIT is still true, so Next still works.
      setHasMore(rows.length > LIMIT);
      setGetdata(rows.slice(0, LIMIT));
    } catch (error) {
      console.error("Fetch Error:", error);
      setGetdata([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    FetchAuction(start, activeKeyword);
  }, [start, activeKeyword, FetchAuction]);

  // =====================================================
  // PAGINATION
  // =====================================================

  const handleNextpage = () => {
    if (!loading && hasMore) {
      setStart((prev) => prev + LIMIT);
    }
  };

  const handleBackpage = () => {
    if (!loading) {
      setStart((prev) => Math.max(0, prev - LIMIT));
    }
  };

  // =====================================================
  // SEARCH
  // =====================================================

  const handleSearchSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    setStart(0);
    setActiveKeyword(inputKeyword.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit(e);
    }
  };

  // =====================================================
  // PRINT
  // =====================================================

  const generateReceipt = (row) => {
    const printWindow = openPrintWindow(1100);

    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildReceipt(row));
    printWindow.document.close();
    printWindow.focus();
  };

  const generateCertificate = (row) => {
    const printWindow = openPrintWindow(1200);

    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildCertificate(row));
    printWindow.document.close();
    printWindow.focus();
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Paper className={classes.root} elevation={0}>
      {/* ========== SEARCH TOOLBAR ========== */}

      <Box className={classes.toolbar}>
        <Box className={classes.searchWrapper}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search Artist or Title..."
            value={inputKeyword}
            onChange={(e) => setInputKeyword(e.target.value)}
            onKeyDown={handleKeyDown}
            className={classes.searchField}
          />

          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SearchIcon />}
            onClick={handleSearchSubmit}
            className={classes.searchButton}
          >
            Search
          </Button>
        </Box>
      </Box>

      {/* ========== TABLE ========== */}

      <TableContainer className={classes.container}>
        <Table stickyHeader size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <StyledTableCell align="left">#</StyledTableCell>
              <StyledTableCell align="left">Artist</StyledTableCell>
              <StyledTableCell align="left">Title</StyledTableCell>
              <StyledTableCell align="left">Medium</StyledTableCell>
              <StyledTableCell align="left">Signed &amp; Date</StyledTableCell>
              <StyledTableCell align="left">Size</StyledTableCell>
              <StyledTableCell align="left">Action</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {getData.length > 0 ? (
              getData.map((row) => (
                <TableRow hover key={row.id} className={classes.tableRow}>
                  <StyledTableCell align="left">
                    <Button
                      size="small"
                      color="primary"
                      className={classes.idButton}
                      href={`/app/private/edit/${encodeURIComponent(
                        row.artist || "no-artist"
                      )}/${row.id}`}
                    >
                      {row.id}
                    </Button>
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <div
                      className={classes.artistText}
                      dangerouslySetInnerHTML={{
                        __html: row.artist || "No Artist Name",
                      }}
                    />
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <Tooltip title={stripHtml(row.title)} arrow>
                      <div
                        className={classes.cellText}
                        dangerouslySetInnerHTML={{ __html: row.title || "" }}
                      />
                    </Tooltip>
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <Tooltip title={stripHtml(row.medium)} arrow>
                      <div
                        className={classes.cellText}
                        dangerouslySetInnerHTML={{ __html: row.medium || "" }}
                      />
                    </Tooltip>
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: row.signed_dated || "",
                      }}
                    />
                  </StyledTableCell>

                  <StyledTableCell align="left" style={{ whiteSpace: "nowrap" }}>
                    <div dangerouslySetInnerHTML={{ __html: row.size || "" }} />
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <div className={classes.actionWrapper}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<ReceiptOutlinedIcon />}
                        className={classes.actionButton}
                        onClick={() => generateReceipt(row)}
                      >
                        Receipt
                      </Button>

                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<CardMembershipOutlinedIcon />}
                        className={classes.actionButton}
                        onClick={() => generateCertificate(row)}
                      >
                        Certificate
                      </Button>
                    </div>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  className={classes.emptyCell}
                >
                  <Typography variant="body2" color="textSecondary">
                    {loading
                      ? "Loading data..."
                      : "No records found matching your search."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ========== PAGINATION ========== */}

      <Box
        className={classes.pagination}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIosIcon fontSize="small" />}
          disabled={start === 0 || loading}
          onClick={handleBackpage}
          className={classes.paginationButton}
        >
          Back
        </Button>

        <Typography className={classes.pageNumber}>
          Page {Math.floor(start / LIMIT) + 1}
        </Typography>

        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowForwardIosIcon fontSize="small" />}
          disabled={loading || !hasMore}
          onClick={handleNextpage}
          className={classes.paginationButton}
        >
          Next
        </Button>
      </Box>

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
}